import {
  BatchStatsResponse,
  ListAuditAnimalResponse,
  ListHealthProtocolsResponse,
  ListHealthProtocolsResponseItem,
  ListSimpleAnimalResponse,
  ListSimpleAnimalResponseSimpleAnimalsItem,
  SyncManagementResponse
} from '@/query';
import Dexie, { Table } from 'dexie';
import { SyncIdentificationDexieService } from './database-service';

export interface IBatchStatsOffline extends BatchStatsResponse {
  batchId: number;
  name: string;
}

export interface Sisbov {
  id?: number;
  sisbov: string;
}

export interface Electronic {
  id?: number;
  electronic: string;
}

export interface Purchase {
  id?: number;
  inicial: string;
  final: string;
}

export interface Unusability {
  id?: number;
  sisbov: string;
}

export interface BatchStats {
  id?: number;
  batchId: number;
  batchStats: IBatchStatsOffline;
}

export interface SimpleAnimal {
  id?: number;
  batchId: number;
  data: ListSimpleAnimalResponse;
}
const syncIdentification = new SyncIdentificationDexieService();

export class OfflineDataDexieService extends Dexie {
  sisbov!: Table<Sisbov>;
  electronic!: Table<Electronic>;
  purchase!: Table<Purchase>;
  unusability!: Table<Unusability>;
  stats!: Table<BatchStats>;
  simpleAnimals!: Table<SimpleAnimal>;
  healthProtocols!: Table<ListHealthProtocolsResponseItem>;

  isSyncing = false;

  constructor() {
    super('feed:offline-data');
    this.version(1).stores({
      sisbov: '++id, sisbov',
      electronic: '++id, electronic',
      purchase: '++id, inicial, final',
      unusability: '++id, sisbov',
      stats: '++id, batchId',
      simpleAnimals: '++id, batchId',
      healthProtocols: '++id, id'
    });
  }

  // ----------------------------------------------------------------------

  private async clearProcessingPendingData() {
    try {
      const processingData = await syncIdentification.pending.where('status').equals('processing').toArray();
      const idsToDelete = processingData.map((item) => item.id!).filter(Boolean);

      if (idsToDelete.length > 0) {
        await syncIdentification.pending.bulkDelete(idsToDelete);
      }
    } catch (error) {
      console.error('Erro ao limpar dados em processamento:', error);
    }
  }

  private async insertAuditAnimalsAsProcessing(listAuditAnimal: ListAuditAnimalResponse) {
    if (!listAuditAnimal || !Array.isArray(listAuditAnimal) || listAuditAnimal.length === 0) return;

    try {
      const newPendingData = listAuditAnimal.map((auditAnimal: any) => {
        let parsedData: any = {};

        try {
          if (auditAnimal.metadata) {
            parsedData = JSON.parse(auditAnimal.metadata);
          }
        } catch (e) {
          console.error('Erro ao fazer parse do metadata do animal auditável:', e);
        }

        return {
          data: parsedData,
          sisbov: parsedData.sisbov ?? null,
          electronic: parsedData.electronic ?? null,
          gtaId: parsedData.gtaId ?? Number(auditAnimal.gta),
          oxId: auditAnimal.oxId,
          timestamp: Date.now(),
          retryCount: 0,
          status: 'processing' as const,
          step: 'identification' as const
        };
      });

      if (newPendingData.length > 0) {
        await syncIdentification.pending.bulkAdd(newPendingData);
      }
    } catch (error) {
      console.error('Erro ao inserir animais auditáveis:', error);
    }
  }

  private async syncAuditAnimals(listAuditAnimal: ListAuditAnimalResponse) {
    await this.clearProcessingPendingData();
    await this.insertAuditAnimalsAsProcessing(listAuditAnimal);
  }

  private async syncHealthProtocols(listHealthProtocols: ListHealthProtocolsResponse) {
    await this.transaction('rw', this.healthProtocols, async () => {
      await this.healthProtocols.clear();

      await this.healthProtocols.bulkAdd(listHealthProtocols);
    });
  }

  async getHealthProtocols(): Promise<ListHealthProtocolsResponseItem[]> {
    return await this.healthProtocols.toArray();
  }

  async sync(
    data: SyncManagementResponse,
    stats: IBatchStatsOffline[],
    simpleAnimalsBatches: ListSimpleAnimalResponse[],
    listAuditAnimal: ListAuditAnimalResponse,
    listHealthProtocols: ListHealthProtocolsResponse
  ) {
    if (this.isSyncing) return;
    this.isSyncing = true;

    const startedAt = performance.now();
    const CHUNK_SIZE = 20_000;

    try {
      // 1) Conta linhas das tabelas em paralelo (sem ler conteúdo).
      const [sisbovCount, electronicCount, unusabilityCount] = await Promise.all([
        this.sisbov.count(),
        this.electronic.count(),
        this.unusability.count()
      ]);

      const shouldSyncSisbov = data.sisbov.length !== sisbovCount;
      const shouldSyncElectronic = data.electronic.length !== electronicCount;
      const shouldSyncUnusability = data.unusability.length !== unusabilityCount;

      // 2) Operações que SEMPRE rodam, em paralelo. Ganho grande aqui:
      //    syncAuditAnimals, syncHealthProtocols, stats clear+bulkAdd,
      //    simpleAnimals clear+bulkAdd e purchase clear+bulkAdd não dependem
      //    umas das outras — antes rodavam serial.
      const purchase = data.purchase.map((item) => ({
        inicial: item.initialSisbov.toString().slice(8, -1),
        final: item.finalSisbov.toString().slice(8, -1)
      }));

      const alwaysOpsPromise = Promise.all([
        this.syncAuditAnimals(listAuditAnimal),
        this.syncHealthProtocols(listHealthProtocols),
        this.transaction('rw', this.stats, async () => {
          await this.stats.clear();
          if (stats.length > 0) {
            await this.stats.bulkAdd(stats.map((item) => ({ batchId: item.batchId, batchStats: item })));
          }
        }),
        this.transaction('rw', this.simpleAnimals, async () => {
          await this.simpleAnimals.clear();
          if (simpleAnimalsBatches.length > 0) {
            await this.simpleAnimals.bulkAdd(simpleAnimalsBatches.map((item) => ({ batchId: item.id, data: item })));
          }
        }),
        this.transaction('rw', this.purchase, async () => {
          await this.purchase.clear();
          if (purchase.length > 0) {
            await this.purchase.bulkAdd(purchase);
          }
        })
      ]);

      // 3) Diff dos datasets pesados (sisbov, electronic, unusability) — só
      //    lê as tabelas existentes quando count diverge (caso contrário
      //    assume-se sem mudanças, cobre 99% das ressincronizações).
      const [newSisbov, electronicDiff, newUnusability] = await Promise.all([
        shouldSyncSisbov
          ? (async () => {
              const existing = new Set((await this.sisbov.toArray()).map((i) => i.sisbov));
              const out: Sisbov[] = [];
              for (const s of data.sisbov) {
                if (!existing.has(s)) out.push({ sisbov: s });
              }
              return out;
            })()
          : Promise.resolve<Sisbov[]>([]),
        shouldSyncElectronic
          ? (async () => {
              const existingEntries = await this.electronic.toArray();
              const existingSet = new Set(existingEntries.map((i) => i.electronic));
              const incomingSet = new Set(data.electronic);

              const newOnes: Electronic[] = [];
              for (const e of data.electronic) {
                if (!existingSet.has(e)) newOnes.push({ electronic: e });
              }

              const removedKeys: number[] = [];
              for (const e of existingEntries) {
                if (e.id != null && !incomingSet.has(e.electronic)) removedKeys.push(e.id);
              }

              return { newOnes, removedKeys };
            })()
          : Promise.resolve<{ newOnes: Electronic[]; removedKeys: number[] }>({ newOnes: [], removedKeys: [] }),
        shouldSyncUnusability
          ? (async () => {
              const existing = new Set((await this.unusability.toArray()).map((i) => i.sisbov));
              const out: Unusability[] = [];
              for (const s of data.unusability) {
                if (!existing.has(s)) out.push({ sisbov: s });
              }
              return out;
            })()
          : Promise.resolve<Unusability[]>([])
      ]);

      // 4) Aguarda as operações sempre-feitas (em paralelo às diffs acima).
      await alwaysOpsPromise;

      const hasDiff =
        newSisbov.length > 0 ||
        electronicDiff.newOnes.length > 0 ||
        electronicDiff.removedKeys.length > 0 ||
        newUnusability.length > 0;

      if (!hasDiff) {
        console.log(`[OfflineSync] sem deltas (${(performance.now() - startedAt).toFixed(0)}ms)`);
        return;
      }

      // 5) Transação única para os deltas.
      await this.transaction('rw', this.sisbov, this.electronic, this.unusability, async () => {
        for (let i = 0; i < newSisbov.length; i += CHUNK_SIZE) {
          await this.sisbov.bulkAdd(newSisbov.slice(i, i + CHUNK_SIZE));
        }
        for (let i = 0; i < electronicDiff.newOnes.length; i += CHUNK_SIZE) {
          await this.electronic.bulkAdd(electronicDiff.newOnes.slice(i, i + CHUNK_SIZE));
        }
        for (let i = 0; i < electronicDiff.removedKeys.length; i += CHUNK_SIZE) {
          await this.electronic.bulkDelete(electronicDiff.removedKeys.slice(i, i + CHUNK_SIZE));
        }
        for (let i = 0; i < newUnusability.length; i += CHUNK_SIZE) {
          await this.unusability.bulkAdd(newUnusability.slice(i, i + CHUNK_SIZE));
        }
      });

      console.log(`[OfflineSync] aplicado em ${(performance.now() - startedAt).toFixed(0)}ms`, {
        newSisbov: newSisbov.length,
        newElectronic: electronicDiff.newOnes.length,
        removedElectronic: electronicDiff.removedKeys.length,
        newUnusability: newUnusability.length
      });
    } finally {
      this.isSyncing = false;
    }
  }

  async findSisbov(data: string): Promise<boolean> {
    const sisbovTrim = data.trim();
    const sisbov = sisbovTrim.length === 16 && sisbovTrim.charAt(0) === '0' ? sisbovTrim.substring(1) : sisbovTrim;

    if (sisbov.length !== 15) {
      throw new Error('O Sisbov deve conter 15 caracteres');
    }

    const sisbovNumber = Number(sisbov.slice(8, -1));
    const found = await this.purchase
      .filter((item) => {
        const inicialNum =
          typeof item.inicial === 'number' ? item.inicial : Number(String(item.inicial).replace(/\D/g, ''));

        const finalNum = typeof item.final === 'number' ? item.final : Number(String(item.final).replace(/\D/g, ''));

        return (
          Number.isFinite(inicialNum) &&
          Number.isFinite(finalNum) &&
          inicialNum <= sisbovNumber &&
          finalNum >= sisbovNumber
        );
      })
      .first();

    if (!found) {
      throw new Error('O Sisbov não está dentro de um pedido de compra');
    }

    const find = await this.sisbov.where('sisbov').equals(sisbov).first();

    if (find?.id) {
      throw new Error('O Sisbov já está em uso');
    }

    const find2 = await this.unusability.where('sisbov').equals(sisbov).first();

    if (find2?.id) {
      throw new Error('O Sisbov está cancelado');
    }

    const find3 = await syncIdentification.findSisbov(Number(sisbov));

    if (find3?.id) {
      throw new Error('O Sisbov já foi processado');
    }

    return true;
  }

  async isSisbovInPurchaseRange(sisbov: string | number): Promise<boolean> {
    const value = typeof sisbov === 'number' ? sisbov : Number(String(sisbov).replace(/\D/g, ''));

    if (!Number.isFinite(value)) {
      return false;
    }

    const found = await this.purchase
      .where('inicial')
      .belowOrEqual(value)
      .and((item) => Number(item.final) >= value)
      .first();

    return !!found;
  }

  async findElectronic(electronic: string): Promise<boolean> {
    const result = await this.electronic.where('electronic').equals(electronic).first();

    if (result?.id) {
      throw new Error('Brinco eletrônico já em uso');
    }

    const findQueue = await syncIdentification.findElectronic(electronic);

    if (findQueue?.id) {
      throw new Error('O brinco eletrônico já foi processado');
    }

    return true;
  }

  async getBatchStats(batchId: number) {
    return await this.stats.where('batchId').equals(batchId).first();
  }

  async getSimpleAnimals(batchId: number): Promise<ListSimpleAnimalResponse> {
    const animals = await this.simpleAnimals.where('batchId').equals(batchId).first();

    if (!animals || animals.data.gtas.length === 0) {
      throw new Error('Nenhuma GTA disponível para esse lote');
    }

    return animals.data;
  }

  /**
   * Retorna todos os animais sincronizados (de qualquer lote) que pertencem à GTA informada. Usado pelo pipeline de
   * identificação no master para conseguir reescolher um candidato mesmo quando o master não está na tela de execução
   * de manejo (store em memória vazio).
   */
  async getSimpleAnimalsByGtaId(gtaId: number): Promise<ListSimpleAnimalResponseSimpleAnimalsItem[]> {
    const allBatches = await this.simpleAnimals.toArray();
    return allBatches.flatMap((batch) => batch.data.simpleAnimals || []).filter((item) => item.ox.gtaId === gtaId);
  }
}
